import React, { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { getBankTransactions } from "../../../services/clearing/bank-transactions";

// Format a Date as an ISO YYYY-MM-DD string using local components
// (avoids the UTC shift that Date.toISOString() would introduce).
const toIsoDate = (date) => {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const firstOfCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const directionOptions = [
  { label: "Alle", value: "" },
  { label: "CREDIT", value: "CREDIT" },
  { label: "DEBIT", value: "DEBIT" },
];

const categoryOptions = [
  { label: "Alle", value: "" },
  { label: "CREDIT_NOTE", value: "CREDIT_NOTE" },
  { label: "BILL", value: "BILL" },
  { label: "BANK", value: "BANK" },
  { label: "THG", value: "THG" },
  { label: "INTERN", value: "INTERN" },
  { label: "STRIPE", value: "STRIPE" },
  { label: "DIVERSE", value: "DIVERSE" },
];

// PrimeReact 10.6.5's Dropdown resolves an option whose value is "" (our "Alle")
// to the whole option object (`option.value || option`), so only a string is a
// real selection; anything else means "Alle". Used by both dropdowns.
const dropdownValue = (value) => (typeof value === "string" ? value : "");

export default function BankTransactionsPage() {
  const [dateRange, setDateRange] = useState([firstOfCurrentMonth(), new Date()]);
  const [direction, setDirection] = useState("");
  const [search, setSearch] = useState("");
  // Client-side only: filters the rows already loaded, never re-queries.
  const [category, setCategory] = useState("");

  // Controlled paginator: PrimeReact keeps its own `first` and does not reset it
  // when the (client-side filtered) value shrinks, so we reset it on every filter.
  const [first, setFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Sorting is controlled too: with a controlled `first`, PrimeReact's own reset to
  // page one on sort has no effect, so onSort resets it (CodeRabbit).
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const from = toIsoDate(dateRange?.[0]);
  const to = toIsoDate(dateRange?.[1]);

  // Debounce the search input so we send one request after typing settles,
  // not one per keystroke (CodeRabbit).
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setFirst(0);
  }, [category, direction, dateRange, debouncedSearch]);

  // Fetch on mount and whenever a filter changes. Only fetch with a complete
  // date range (both ends selected) or no range at all. The `ignore` flag drops
  // stale responses so a slower earlier request can't overwrite newer state.
  useEffect(() => {
    const rangeIncomplete = dateRange && dateRange[0] && !dateRange[1];
    if (rangeIncomplete) return undefined;

    let ignore = false;
    setLoading(true);
    setError(null);

    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (direction) params.direction = direction;
    if (debouncedSearch && debouncedSearch.trim()) params.search = debouncedSearch.trim();

    getBankTransactions(params)
      .then((data) => {
        if (!ignore) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (ignore) return;
        setError("Die Banktransaktionen konnten nicht geladen werden.");
        setRows([]);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [from, to, direction, debouncedSearch, dateRange]);

  const directionTemplate = (row) => {
    if (row.direction === "CREDIT") {
      return <Tag value="CREDIT" severity="success" />;
    }
    if (row.direction === "DEBIT") {
      return <Tag value="DEBIT" severity="danger" />;
    }
    return row.direction ?? "-";
  };

  const categoryTemplate = (row) => {
    if (row.cochargeCategory === "CREDIT_NOTE") {
      return <Tag value="CREDIT_NOTE" severity="danger" />;
    }
    if (row.cochargeCategory === "BILL") {
      return <Tag value="BILL" severity="success" />;
    }
    // PrimeReact's Tag has only four severities, so BANK, INTERN and STRIPE
    // get their colour inline.
    if (row.cochargeCategory === "BANK") {
      return <Tag value="BANK" style={{ background: "#000", color: "#fff" }} />;
    }
    if (row.cochargeCategory === "THG") {
      return <Tag value="THG" severity="info" />;
    }
    if (row.cochargeCategory === "INTERN") {
      return <Tag value="INTERN" style={{ background: "#6c757d", color: "#fff" }} />;
    }
    if (row.cochargeCategory === "STRIPE") {
      return <Tag value="STRIPE" style={{ background: "#635bff", color: "#fff" }} />;
    }
    if (row.cochargeCategory === "DIVERSE") {
      return <Tag value="DIVERSE" severity="warning" />;
    }
    return row.cochargeCategory ?? "-";
  };

  const visibleRows = useMemo(
    () => (category ? rows.filter((r) => r.cochargeCategory === category) : rows),
    [rows, category],
  );

  const header = useMemo(
    () => (
      <div className="flex flex-column md:flex-row md:align-items-end gap-3">
        <div className="flex flex-column gap-1">
          <label htmlFor="bank-tx-range" className="text-sm font-medium">
            Zeitraum
          </label>
          <Calendar
            inputId="bank-tx-range"
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            dateFormat="dd.mm.yy"
            placeholder="Zeitraum wählen"
            showIcon
          />
        </div>

        <div className="flex flex-column gap-1">
          <label htmlFor="bank-tx-direction" className="text-sm font-medium">
            Richtung
          </label>
          <Dropdown
            inputId="bank-tx-direction"
            value={direction}
            options={directionOptions}
            onChange={(e) => setDirection(dropdownValue(e.value))}
            placeholder="Alle"
          />
        </div>

        <div className="flex flex-column gap-1">
          <label htmlFor="bank-tx-category" className="text-sm font-medium">
            cocharge category
          </label>
          <Dropdown
            inputId="bank-tx-category"
            value={category}
            options={categoryOptions}
            onChange={(e) => setCategory(dropdownValue(e.value))}
            placeholder="Alle"
          />
        </div>

        <div className="flex flex-column gap-1 flex-grow-1">
          <label htmlFor="bank-tx-search" className="text-sm font-medium">
            Suche
          </label>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              id="bank-tx-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche (Verwendungszweck, Gegenpartei, IBAN)"
              className="w-full"
            />
          </span>
        </div>
      </div>
    ),
    [dateRange, direction, category, search],
  );

  return (
    <div className="layout-main">
      <div className="surface-section">
        <div className="grid">
          <div className="col-12">
            <div className="flex justify-content-between align-items-center mb-4">
              <h2 className="m-0">Bank Transactions</h2>
            </div>

            {error && (
              <Message severity="error" text={error} className="mb-3 w-full" />
            )}

            <Card>
              <DataTable
                value={visibleRows}
                header={header}
                loading={loading}
                paginator
                first={first}
                rows={rowsPerPage}
                onPage={(e) => {
                  setFirst(e.first);
                  setRowsPerPage(e.rows);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                sortMode="single"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={(e) => {
                  setSortField(e.sortField);
                  setSortOrder(e.sortOrder);
                  setFirst(0);
                }}
                removableSort
                emptyMessage="Keine Banktransaktionen gefunden"
                responsiveLayout="scroll"
                dataKey="bankTxId"
              >
                <Column
                  field="cochargeCategory"
                  header="cocharge category"
                  body={categoryTemplate}
                  sortable
                />
                <Column field="bookingDate" header="Buchungsdatum" sortable />
                <Column field="valueDate" header="Value-Date" sortable />
                <Column field="amount" header="Betrag" sortable />
                <Column field="currency" header="Währung" sortable />
                <Column
                  field="direction"
                  header="Richtung"
                  body={directionTemplate}
                  sortable
                />
                <Column
                  field="counterpartyName"
                  header="Gegenpartei-Name"
                  sortable
                />
                <Column
                  field="counterpartyIban"
                  header="Gegenpartei-IBAN"
                  sortable
                />
                <Column
                  field="remittanceInfo"
                  header="Verwendungszweck"
                  sortable
                />
                <Column field="bankTxCode" header="Bank-Tx-Code" sortable />
                <Column
                  field="entryDetailsCount"
                  header="Sammelbuchungen"
                  sortable
                />
                <Column field="statementId" header="Statement-ID" sortable />
                <Column field="bankTxId" header="Bank-Tx-ID" sortable />
              </DataTable>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
